import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@exhousing/design-system";
import { Check, Download, Eye, FileText, Trash2 } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { DocumentPreviewDialog } from "@/components/document/DocumentPreviewDialog";
import { RecipientChip } from "@/components/document/RecipientChip";
import {
  type RenderParams,
  fetchDocumentDownloadUrl,
  useDeleteDocument,
  useDocuments,
  useGenerateDocument,
  useUpdateDocumentStatus,
} from "@/hooks/api/useDocuments";
import type { components } from "@/types/api";

import { leaseModelLabel } from "@/lib/leaseModels";

type Contract = components["schemas"]["ContractResponse"];

// The lease model is a property of the contract (set on the Terms tab), so it is not
// re-picked here. Other doc types carry no variant.
const DOC_TYPES: { value: string; label: string }[] = [
  { value: "rental_contract", label: "Rental contract" },
  { value: "booking_confirmation", label: "Confirmation letter" },
  { value: "rent_invoice", label: "Invoice" },
  { value: "rent_receipt", label: "Rent receipt" },
  { value: "indexation_letter", label: "Indexation letter" },
  { value: "deposit_invoice", label: "Deposit invoice" },
  { value: "deposit_return", label: "Deposit return" },
];

const SELECT =
  "h-9 rounded-md border border-input bg-background px-2.5 text-sm outline-none focus:border-primary";

function formatDate(value: string): string {
  return new Date(value).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
}

export function DocumentsCard({ contract }: { contract: Contract }) {
  const contractId = contract.id;
  const leaseVariant = contract.lease_variant ?? "roz";
  const { data: documents } = useDocuments("contract", contractId);
  const generate = useGenerateDocument("contract", contractId);
  const remove = useDeleteDocument("contract", contractId);
  const setStatus = useUpdateDocumentStatus("contract", contractId);

  const [type, setType] = useState("rental_contract");
  const [language, setLanguage] = useState("nl");
  const [preview, setPreview] = useState<RenderParams | null>(null);

  const isLease = type === "rental_contract";

  async function download(id: string) {
    try {
      const url = await fetchDocumentDownloadUrl(id);
      window.open(url, "_blank", "noopener");
    } catch {
      toast.error("Could not open the document");
    }
  }

  function runGenerate() {
    if (!preview) return;
    generate.mutate(
      { type: preview.type, variant: preview.variant, language: preview.language },
      {
        onSuccess: (res) => {
          toast.success("Document generated");
          setPreview(null);
          window.open(res.download_url, "_blank", "noopener");
        },
        onError: () => toast.error("Could not generate the document"),
      },
    );
  }

  return (
    <Card>
      <CardHeader className="space-y-1">
        <div className="flex items-center justify-between">
          <CardTitle>Documents</CardTitle>
          {documents && documents.length > 0 && (
            <Badge tone="neutral">{documents.length}</Badge>
          )}
        </div>
        <p className="text-sm text-muted-foreground">
          Generate and store the lease, letters and invoices for this tenancy.
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        <form
          className="flex flex-wrap items-end gap-2"
          onSubmit={(e) => {
            e.preventDefault();
            setPreview({
              type,
              // The lease uses the contract's model; other types carry no variant.
              variant: isLease ? leaseVariant : "",
              language,
              subject_type: "contract",
              subject_id: contractId,
            });
          }}
        >
          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Type</span>
            <select
              className={SELECT}
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              {DOC_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </label>

          {isLease && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-muted-foreground">Model</span>
              <span
                className="flex h-9 items-center text-sm"
                title="Set on the Terms tab"
              >
                {leaseModelLabel(leaseVariant)}
              </span>
            </div>
          )}

          <label className="flex flex-col gap-1">
            <span className="text-xs text-muted-foreground">Language</span>
            <select
              className={SELECT}
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="nl">Nederlands</option>
              <option value="en">English</option>
            </select>
          </label>

          <Button type="submit" size="sm">
            <Eye className="size-4" />
            Preview & generate
          </Button>
        </form>

        {isLease && (
          <p className="-mt-1 text-xs text-muted-foreground">
            The lease uses this contract's model ({leaseModelLabel(leaseVariant)}). Change
            it on the Terms tab.
          </p>
        )}

        {documents && documents.length > 0 ? (
          <ul className="divide-y divide-border/60">
            {documents.map((doc) => (
              <li key={doc.id} className="flex items-center justify-between gap-3 py-2">
                <div className="flex min-w-0 items-center gap-2.5">
                  <FileText className="size-4 shrink-0 text-muted-foreground" />
                  <div className="min-w-0">
                    <p className="flex items-center gap-2 text-sm font-medium">
                      <span className="truncate">{doc.title}</span>
                      {doc.status === "draft" && (
                        <Badge tone="warning" dot>
                          Draft
                        </Badge>
                      )}
                    </p>
                    <p className="flex flex-wrap items-center gap-x-1.5 text-xs text-muted-foreground">
                      <span>
                        {doc.type.replace(/_/g, " ")}
                        {doc.variant ? ` · ${doc.variant.toUpperCase()}` : ""} ·{" "}
                        {formatDate(doc.created_at)}
                      </span>
                      <RecipientChip recipient={doc.recipient} />
                    </p>
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-1">
                  {doc.status === "draft" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={setStatus.isPending}
                      onClick={() =>
                        setStatus.mutate(
                          { documentId: doc.id, status: "final" },
                          {
                            onSuccess: () => toast.success("Marked final"),
                            onError: () => toast.error("Could not update the document"),
                          },
                        )
                      }
                    >
                      <Check className="size-4 text-success" />
                      Mark final
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => download(doc.id)}
                    aria-label="Download"
                  >
                    <Download className="size-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      remove.mutate(doc.id, {
                        onError: () => toast.error("Could not delete the document"),
                      })
                    }
                    aria-label="Delete"
                  >
                    <Trash2 className="size-4 text-destructive" />
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <p className="py-2 text-sm text-muted-foreground">
            No documents yet — pick a type above and preview it before generating.
          </p>
        )}
      </CardContent>

      <DocumentPreviewDialog
        params={preview}
        onClose={() => setPreview(null)}
        onGenerate={runGenerate}
        generating={generate.isPending}
      />
    </Card>
  );
}
